import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, AlertCircle, ChevronRight, Loader2,
  Trash2, ExternalLink, Eye, EyeOff, RefreshCw, Plus, Building2,
  ArrowRightLeft, ShieldAlert, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

// ─── Logo provider (Clearbit CDN + fallback SVG) ─────────────────────────────

// ─── Logos SVG inline (fallback fidèle aux marques) ─────────────────────────

const LogoPennyLane = ({ size }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg" className="rounded-xl flex-shrink-0">
    <rect width="200" height="200" rx="32" fill="#FFFFFF" />
    <rect width="200" height="200" rx="32" fill="#00CF9B" opacity="0.12" />
    {/* P stylisé PennyLane */}
    <rect x="55" y="42" width="28" height="116" rx="8" fill="#00B37D" />
    <path d="M55 42h52c22 0 38 16 38 38s-16 38-38 38H83V94h24c5 0 10-4 10-10s-5-10-10-10H83V42z" fill="#00B37D" />
    <circle cx="107" cy="84" r="12" fill="#FFFFFF" />
  </svg>
);

const LogoOdoo = ({ size }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg" className="rounded-xl flex-shrink-0">
    <rect width="200" height="200" rx="32" fill="#FFFFFF" />
    {/* Odoo : trois cercles colorés (icône officielle) */}
    <circle cx="100" cy="66" r="34" fill="#875A7B" />
    <circle cx="56"  cy="140" r="34" fill="#56B3B4" />
    <circle cx="144" cy="140" r="34" fill="#E16D6D" />
    {/* Trous intérieurs */}
    <circle cx="100" cy="66"  r="16" fill="#FFFFFF" />
    <circle cx="56"  cy="140" r="16" fill="#FFFFFF" />
    <circle cx="144" cy="140" r="16" fill="#FFFFFF" />
  </svg>
);

const LogoResend = ({ size }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg" className="rounded-xl flex-shrink-0">
    <rect width="200" height="200" rx="32" fill="#0F172A" />
    {/* R stylisé Resend */}
    <rect x="55" y="44" width="26" height="112" rx="6" fill="#FFFFFF" />
    <path d="M55 44h52c22 0 38 15 38 36s-14 35-34 36l38 40h-34l-34-38H81v38H55V44z" fill="#FFFFFF" />
    <circle cx="107" cy="80" r="13" fill="#0F172A" />
  </svg>
);

const LogoPappers = ({ size }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg" className="rounded-xl flex-shrink-0">
    <rect width="200" height="200" rx="32" fill="#1D4ED8" />
    {/* P stylisé Pappers */}
    <rect x="54" y="44" width="26" height="112" rx="7" fill="#FFFFFF" />
    <path d="M54 44h54c24 0 40 17 40 40s-16 40-40 40H82V98h26c8 0 14-6 14-14s-6-14-14-14H80V44z" fill="#FFFFFF" />
    <circle cx="108" cy="84" r="10" fill="#1D4ED8" />
  </svg>
);

const SVG_LOGOS = { pennylane: LogoPennyLane, odoo: LogoOdoo, resend: LogoResend, pappers: LogoPappers };

const BRAND_COLORS = {
  pennylane: '#00B37D',
  odoo:      '#875A7B',
  resend:    '#0F172A',
  pappers:   '#1D4ED8',
};

function ProviderLogo({ provider, logoDomain, name, size = 44 }) {
  const [imgError, setImgError] = useState(false);
  const color = BRAND_COLORS[provider] || '#1A3A6E';
  const initial = (name || provider || '?')[0].toUpperCase();

  // Composant SVG inline fidèle à la marque
  const SvgLogo = SVG_LOGOS[provider];
  if (SvgLogo) {
    return <SvgLogo size={size} />;
  }

  // Fallback : initiale colorée
  return (
    <div className="flex-shrink-0 flex items-center justify-center rounded-xl font-bold text-white"
      style={{ width: size, height: size, background: color, fontSize: size * 0.38 }}>
      {initial}
    </div>
  );
}

// ─── Champ mot de passe avec toggle visibilité ────────────────────────────────

function PasswordField({ id, label, value, onChange, placeholder, required }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
        style={{ color: 'var(--text-secondary)' }}>
        {label}{required && <span style={{ color: '#DC2626' }}> *</span>}
      </label>
      <div className="relative">
        <input
          id={id}
          data-testid={`integration-field-${id}`}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg px-3 py-2 text-sm pr-10 outline-none"
          style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text)' }}
          autoComplete="new-password"
        />
        <button type="button" onClick={() => setShow(v => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2"
          style={{ color: 'var(--text-muted)' }}>
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}

function TextField({ id, label, value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
        style={{ color: 'var(--text-secondary)' }}>
        {label}{required && <span style={{ color: '#DC2626' }}> *</span>}
      </label>
      <input
        data-testid={`integration-field-${id}`}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg px-3 py-2 text-sm outline-none"
        style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text)' }}
        autoComplete="off"
      />
    </div>
  );
}

function TestResult({ result }) {
  if (!result) return null;
  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-sm"
      style={{
        background: result.ok ? '#F0FDF4' : '#FEF2F2',
        border: `1px solid ${result.ok ? '#BBF7D0' : '#FECACA'}`,
        color: result.ok ? '#15803D' : '#DC2626',
      }}>
      {result.ok
        ? <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
        : <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
      }
      <span className="text-xs leading-relaxed">{result.message}</span>
    </div>
  );
}

// ─── Mappings TVA par logiciel ────────────────────────────────────────────────

const TVA_PROVIDERS = [
  { key: 'pennylane', label: 'PennyLane',  icon: <LogoPennyLane size={13} /> },
  { key: 'sage',      label: 'Sage',        icon: null },
  { key: 'quickbooks',label: 'QuickBooks',  icon: null },
];

function TvaMappingsSection() {
  const [activeProvider, setActiveProvider] = useState('pennylane');
  const [rows, setRows]       = useState([]);
  const [draft, setDraft]     = useState({});   // { tva_id: { external_code, compte_collecte, compte_deductible } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [showComptes, setShowComptes] = useState(false);

  const load = useCallback(async (provider) => {
    setLoading(true);
    try {
      const r = await api.getTvaMappings(provider);
      const data = r.data.taux_tva || [];
      setRows(data);
      const init = {};
      data.forEach(row => {
        init[row.tva_id] = {
          external_code:     row.external_code     || '',
          compte_collecte:   row.compte_collecte   || '',
          compte_deductible: row.compte_deductible || '',
        };
      });
      setDraft(init);
    } catch {
      toast.error('Impossible de charger les mappings TVA');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(activeProvider); }, [load, activeProvider]);

  const handleProviderChange = (p) => {
    setActiveProvider(p);
  };

  const handleChange = (tva_id, field, value) => {
    setDraft(prev => ({
      ...prev,
      [tva_id]: { ...prev[tva_id], [field]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const mappings = rows.map(row => ({
        tva_id:            row.tva_id,
        external_code:     draft[row.tva_id]?.external_code     || '',
        compte_collecte:   draft[row.tva_id]?.compte_collecte   || '',
        compte_deductible: draft[row.tva_id]?.compte_deductible || '',
      })).filter(m => m.external_code);

      await api.saveTvaMappings({ provider: activeProvider, mappings });
      toast.success('Mappings TVA enregistrés');
      await load(activeProvider);
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const isDirty = rows.some(row => {
    const d = draft[row.tva_id] || {};
    return (d.external_code || '') !== (row.external_code || '')
        || (d.compte_collecte || '') !== (row.compte_collecte || '')
        || (d.compte_deductible || '') !== (row.compte_deductible || '');
  });

  return (
    <section data-testid="tva-mappings-section" className="mb-10">

      {/* Titre */}
      <div className="flex items-center gap-2 mb-1">
        <Hash size={15} style={{ color: 'var(--text-secondary)' }} />
        <p className="text-xs font-semibold uppercase tracking-widest"
          style={{ color: 'var(--text-disabled)' }}>Mappings TVA par logiciel comptable</p>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
        Associez chaque taux TVA interne au code attendu par l'API du logiciel cible.
        Ces codes remplacent la correspondance automatique lors de l'export des factures.
      </p>

      {/* Onglets providers */}
      <div className="flex items-center gap-1 mb-4">
        {TVA_PROVIDERS.map(p => (
          <button key={p.key}
            data-testid={`tva-provider-tab-${p.key}`}
            onClick={() => handleProviderChange(p.key)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{
              background:  activeProvider === p.key ? 'var(--accent-muted)' : 'transparent',
              color:       activeProvider === p.key ? 'var(--accent)'       : 'var(--text-muted)',
              border:      activeProvider === p.key ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
              cursor: 'pointer',
            }}>
            {p.icon}
            {p.label}
          </button>
        ))}
        <button
          onClick={() => setShowComptes(v => !v)}
          className="ml-auto text-xs px-3 py-1.5 rounded-lg transition-all"
          style={{
            color: 'var(--text-muted)',
            border: '1px solid var(--border)',
            background: 'transparent',
            cursor: 'pointer',
          }}>
          {showComptes ? 'Masquer comptes' : 'Afficher comptes'}
        </button>
      </div>

      {/* Table */}
      <div className="rounded-2xl overflow-hidden"
        style={{ border: '1px solid var(--border)', background: 'var(--card)' }}>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={16} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)', width: '30%' }}>Taux TVA interne</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)', width: '20%' }}>Territoire</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)' }}>
                  Code {TVA_PROVIDERS.find(p => p.key === activeProvider)?.label}
                </th>
                {showComptes && <>
                  <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)' }}>Cpt collectée</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)' }}>Cpt déductible</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.tva_id} data-testid={`tva-row-${row.tva_id}`}
                  style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}>

                  {/* Taux interne */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-sm"
                        style={{ color: 'var(--text)' }}>
                        {row.taux % 1 === 0 ? row.taux.toFixed(0) : row.taux}%
                      </span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {row.label}
                      </span>
                    </div>
                  </td>

                  {/* Territoire */}
                  <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                    {row.territoire || '—'}
                  </td>

                  {/* Code externe */}
                  <td className="px-4 py-2.5">
                    <input
                      data-testid={`tva-code-input-${row.tva_id}`}
                      value={draft[row.tva_id]?.external_code || ''}
                      onChange={e => handleChange(row.tva_id, 'external_code', e.target.value)}
                      placeholder="ex: FR_200, reduced, TAX_0012"
                      className="w-full px-3 py-1.5 rounded-lg text-xs font-mono"
                      style={{
                        border: '1px solid var(--border)',
                        background: 'var(--input-bg, var(--bg))',
                        color: 'var(--text)',
                        outline: 'none',
                      }}
                    />
                  </td>

                  {showComptes && <>
                    <td className="px-4 py-2.5">
                      <input
                        value={draft[row.tva_id]?.compte_collecte || ''}
                        onChange={e => handleChange(row.tva_id, 'compte_collecte', e.target.value)}
                        placeholder="ex: 44571"
                        className="w-full px-3 py-1.5 rounded-lg text-xs font-mono"
                        style={{
                          border: '1px solid var(--border)',
                          background: 'var(--input-bg, var(--bg))',
                          color: 'var(--text)',
                          outline: 'none',
                        }}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <input
                        value={draft[row.tva_id]?.compte_deductible || ''}
                        onChange={e => handleChange(row.tva_id, 'compte_deductible', e.target.value)}
                        placeholder="ex: 44566"
                        className="w-full px-3 py-1.5 rounded-lg text-xs font-mono"
                        style={{
                          border: '1px solid var(--border)',
                          background: 'var(--input-bg, var(--bg))',
                          color: 'var(--text)',
                          outline: 'none',
                        }}
                      />
                    </td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer save */}
      {!loading && (
        <div className="flex justify-end mt-3">
          <button
            data-testid="tva-mappings-save-btn"
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            style={{
              background:  saving || !isDirty ? 'var(--text-disabled)' : 'var(--accent)',
              cursor:      saving || !isDirty ? 'not-allowed' : 'pointer',
            }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Enregistrer les mappings
          </button>
        </div>
      )}
    </section>
  );
}

// ─── Routage comptable par société ───────────────────────────────────────────

const PROVIDER_LABELS = {
  odoo:      { label: 'Odoo',       color: '#875A7B', bg: '#F5F0F4' },
  pennylane: { label: 'PennyLane',  color: '#00B37D', bg: '#F0FDF8' },
  aucun:     { label: 'Aucun',      color: '#9CA3AF', bg: '#F9FAFB' },
};

function RoutingSection() {
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});   // { societe_id: bool }
  const [confirmChange, setConfirmChange] = useState(null); // { societe_id, nom, target }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getIntegrationRouting();
      setSocietes(r.data.societes || []);
    } catch {
      toast.error('Impossible de charger le routage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChangeProvider = async (societe_id, provider_comptable) => {
    setSaving(s => ({ ...s, [societe_id]: true }));
    try {
      await api.updateIntegrationRouting(societe_id, { provider_comptable });
      setSocietes(prev => prev.map(s =>
        s.societe_id === societe_id ? { ...s, provider_comptable } : s
      ));
      toast.success('Routage mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(s => ({ ...s, [societe_id]: false }));
    }
  };

  const handleClick = (soc, target) => {
    if (soc.provider_comptable === target) return;
    // Si on bascule vers Pennylane sans clé → demander confirmation
    if (target === 'pennylane' && !soc.pl_configured) {
      setConfirmChange({ societe_id: soc.societe_id, nom: soc.nom, target });
    } else {
      handleChangeProvider(soc.societe_id, target);
    }
  };

  const [syncingPlanComptable, setSyncingPlanComptable] = useState(false);

  const handleSyncPlanComptable = async () => {
    setSyncingPlanComptable(true);
    try {
      const r = await api.syncPlPlanComptable();
      const results = r.data?.results || [];
      const ok = results.filter(x => x.ok);
      const total = ok.reduce((s, x) => s + (x.synced || 0), 0);
      toast.success(`Plan comptable synchronisé — ${total} compte(s) sur ${ok.length} dossier(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la synchronisation du plan comptable');
    } finally {
      setSyncingPlanComptable(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-10">
      <Loader2 size={18} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
    </div>
  );

  if (societes.length === 0) return null;

  const nbPennylane = societes.filter(s => s.provider_comptable === 'pennylane').length;
  const nbOdoo     = societes.filter(s => s.provider_comptable === 'odoo').length;

  return (
    <section data-testid="routing-section" className="mb-10">

      {/* Titre */}
      <div className="flex items-center gap-2 mb-1">
        <ArrowRightLeft size={15} style={{ color: 'var(--text-secondary)' }} />
        <p className="text-xs font-semibold uppercase tracking-widest"
          style={{ color: 'var(--text-disabled)' }}>Routage comptable par société</p>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
        Choisissez l'outil de comptabilité actif pour chaque société.
        Les factures seront créées dans l'outil sélectionné.
      </p>

      {/* Résumé */}
      <div className="flex gap-3 mb-4">
        {nbOdoo > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ background: '#F5F0F4', color: '#875A7B', border: '1px solid #D8C3D8' }}>
            <LogoOdoo size={13} /> {nbOdoo} société{nbOdoo > 1 ? 's' : ''} · Odoo
          </span>
        )}
        {nbPennylane > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ background: '#F0FDF8', color: '#00B37D', border: '1px solid #A7F3D0' }}>
            <LogoPennyLane size={13} /> {nbPennylane} société{nbPennylane > 1 ? 's' : ''} · PennyLane
          </span>
        )}
        {nbPennylane > 0 && (
          <button
            data-testid="pl-sync-plan-comptable-btn"
            onClick={handleSyncPlanComptable}
            disabled={syncingPlanComptable}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-colors"
            style={{ border: '1px solid #00B37D', color: '#00B37D', background: 'transparent', opacity: syncingPlanComptable ? 0.6 : 1 }}>
            {syncingPlanComptable
              ? <><Loader2 size={11} className="animate-spin" /> Sync en cours…</>
              : <><RefreshCw size={11} /> Sync plan comptable PL</>
            }
          </button>
        )}
      </div>

      {/* Tableau */}
      <div className="rounded-2xl overflow-hidden"
        style={{ border: '1px solid var(--border)', background: 'var(--card)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
              <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--text-muted)', width: '35%' }}>Société</th>
              <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>Outil comptable actif</th>
              <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}>Clé PennyLane</th>
            </tr>
          </thead>
          <tbody>
            {societes.map((soc, i) => {
              const isSaving = saving[soc.societe_id];
              return (
                <tr key={soc.societe_id} data-testid={`routing-row-${soc.societe_id}`}
                  style={{ borderBottom: i < societes.length - 1 ? '1px solid var(--border)' : 'none' }}>

                  {/* Société */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: '#1A3A6E' }}>
                        {(soc.nom || '?')[0]}
                      </div>
                      <span className="font-medium text-sm" style={{ color: 'var(--text)' }}>
                        {soc.nom}
                      </span>
                    </div>
                  </td>

                  {/* Sélecteur Odoo / PennyLane */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5">
                      {['odoo', 'pennylane'].map(p => {
                        const meta = PROVIDER_LABELS[p];
                        const active = soc.provider_comptable === p;
                        return (
                          <button key={p}
                            data-testid={`routing-btn-${soc.societe_id}-${p}`}
                            onClick={() => handleClick(soc, p)}
                            disabled={isSaving}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                            style={{
                              background:  active ? meta.bg    : 'transparent',
                              color:       active ? meta.color : 'var(--text-muted)',
                              border:      active ? `1.5px solid ${meta.color}` : '1.5px solid var(--border)',
                              cursor:      isSaving ? 'not-allowed' : 'pointer',
                              opacity:     isSaving ? 0.6 : 1,
                            }}>
                            {isSaving && active && <Loader2 size={10} className="animate-spin" />}
                            {p === 'odoo'      && <LogoOdoo size={13} />}
                            {p === 'pennylane' && <LogoPennyLane size={13} />}
                            {meta.label}
                          </button>
                        );
                      })}
                    </div>
                  </td>

                  {/* Statut clé PennyLane */}
                  <td className="px-5 py-3.5">
                    {soc.pl_configured ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium"
                        style={{ color: '#15803D' }}>
                        <CheckCircle2 size={12} /> Configurée
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs"
                        style={{ color: 'var(--text-disabled)' }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 inline-block" />
                        Non configurée
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modale de confirmation (bascule vers PL sans clé) */}
      {confirmChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.35)' }}
          onClick={() => setConfirmChange(null)}>
          <div className="rounded-2xl p-6 max-w-sm w-full mx-4"
            style={{ background: 'var(--card)', border: '1px solid var(--border)',
                     boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <ShieldAlert size={20} style={{ color: '#F59E0B', flexShrink: 0, marginTop: 2 }} />
              <div>
                <p className="font-semibold text-sm mb-1" style={{ color: 'var(--text)' }}>
                  Clé PennyLane manquante
                </p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                  La société <strong>{confirmChange.nom}</strong> n'a pas encore de clé PennyLane configurée.
                  Basculer quand même ? Les factures échoueront jusqu'à ce que la clé soit ajoutée.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmChange(null)}
                className="flex-1 py-2 rounded-lg text-sm font-medium"
                style={{ border: '1px solid var(--border)', color: 'var(--text-muted)', background: 'transparent' }}>
                Annuler
              </button>
              <button
                onClick={() => { handleChangeProvider(confirmChange.societe_id, confirmChange.target); setConfirmChange(null); }}
                className="flex-1 py-2 rounded-lg text-sm font-semibold text-white"
                style={{ background: '#F59E0B' }}>
                Basculer quand même
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Drawer PennyLane multi-société ──────────────────────────────────────────

function PennyLaneDrawer({ integration, meta, onClose, onSaved }) {
  const [companies, setCompanies] = useState([]);
  const [keys, setKeys] = useState({});        // { societe_id: api_key }
  const [autoCreate, setAutoCreate] = useState({}); // { societe_id: bool }
  const [testing, setTesting] = useState({});  // { id: bool }
  const [results, setResults] = useState({});  // { id: {ok, msg} }
  const [syncing, setSyncing] = useState({});  // { societe_id: bool }
  const [syncResults, setSyncResults] = useState({}); // { societe_id: result }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getPennyLaneCompanies();
      const list = r.data.companies || [];
      setCompanies(list);
      const init = {};
      const ac = {};
      list.forEach(c => {
        init[c.societe_id] = c.api_key_masked || '';
        ac[c.societe_id]   = c.auto_create    || false;
      });
      setKeys(init);
      setAutoCreate(ac);
    } catch {
      toast.error('Impossible de charger les sociétés');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const handleTest = async (comp) => {
    const key = keys[comp.societe_id] || '';
    if (!key) {
      setResults(r => ({ ...r, [comp.societe_id]: { ok: false, message: 'Veuillez saisir une clé API avant de tester.' } }));
      return;
    }
    setTesting(t => ({ ...t, [comp.societe_id]: true }));
    try {
      const r = await api.testIntegration('pennylane', { config: { api_key: key, societe_id: comp.societe_id } });
      setResults(p => ({ ...p, [comp.societe_id]: r.data }));
    } catch (e) {
      setResults(p => ({ ...p, [comp.societe_id]: { ok: false, message: e.response?.data?.detail || 'Erreur réseau' } }));
    } finally {
      setTesting(t => ({ ...t, [comp.societe_id]: false }));
    }
  };


  const handleSyncTiers = async (comp) => {    setSyncing(s => ({ ...s, [comp.societe_id]: true }));
    setSyncResults(s => ({ ...s, [comp.societe_id]: { status: 'running', progress: 'Lancement…' } }));
    try {
      const r = await api.syncTiersPennylane(comp.societe_id);
      const jobId = r.data.job_id;
      if (!jobId) {
        // Ancienne réponse synchrone (compatibilité)
        setSyncResults(s => ({ ...s, [comp.societe_id]: r.data }));
        setSyncing(s => ({ ...s, [comp.societe_id]: false }));
        toast.success(`Synchronisation terminée — ${r.data.synced} tiers associés`);
        return;
      }
      // Polling toutes les 2s jusqu'à completion
      let _pollRetries = 0;
      const poll = async () => {
        try {
          const sr = await api.getSyncTiersStatus(jobId);
          const st = sr.data;
          setSyncResults(s => ({ ...s, [comp.societe_id]: st }));
          if (st.status === 'running') {
            setTimeout(poll, 2000);
          } else if (st.status === 'done') {
            setSyncing(s => ({ ...s, [comp.societe_id]: false }));
            toast.success(`Synchronisation terminée — ${st.synced} tiers associés`);
          } else {
            setSyncing(s => ({ ...s, [comp.societe_id]: false }));
            toast.error(st.message || 'Erreur lors de la synchronisation');
          }
        } catch (e) {
          const httpStatus = e.response?.status;
          const detail = e.response?.data?.detail;
          // Retry jusqu'à 3x si le job n'est pas encore visible (ex: redémarrage backend)
          if (httpStatus === 404 && _pollRetries < 3) {
            _pollRetries++;
            setTimeout(poll, 3000);
            return;
          }
          setSyncing(s => ({ ...s, [comp.societe_id]: false }));
          toast.error(detail || e.message || 'Erreur lors du suivi de la synchronisation');
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur lors de la synchronisation';
      setSyncResults(s => ({ ...s, [comp.societe_id]: { ok: false, message: msg } }));
      setSyncing(s => ({ ...s, [comp.societe_id]: false }));
      toast.error(msg);
    }
  };

  const handleSave = async () => {
    if (companies.length === 0) {
      toast.error('Aucune société configurée dans le Transfert Comptable. Ajoutez d\'abord une société dans l\'onglet Transfert Comptable.');
      return;
    }
    setSaving(true);
    try {
      const companiesPayload = {};
      companies.forEach(c => {
        companiesPayload[c.societe_id] = {
          nom:         c.nom,
          api_key:     keys[c.societe_id] || '',
          auto_create: autoCreate[c.societe_id] || false,
        };
      });
      await api.saveIntegration('pennylane', {
        config: { companies: companiesPayload },
        is_active: true,
      });
      toast.success('Clés PennyLane enregistrées');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteIntegration('pennylane');
      toast.success('Intégration PennyLane supprimée');
      onSaved();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const isConfigured = !!integration?.connected_at;
  const color = BRAND_COLORS.pennylane;

  return (
    <div data-testid="integration-drawer-pennylane"
      className="fixed inset-y-0 right-0 z-50 flex flex-col"
      style={{ width: 598, background: 'var(--card)', boxShadow: '-4px 0 40px rgba(0,0,0,0.13)', borderLeft: '1px solid var(--border)' }}>

      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-5" style={{ borderBottom: '1px solid var(--border)' }}>
        <ProviderLogo provider="pennylane" logoDomain={meta?.logo_domain} name={meta?.name} size={40} />
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-base" style={{ color: 'var(--text)' }}>{meta?.name}</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Clé API par société</p>
        </div>
        <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-gray-100 transition-colors" data-testid="integration-drawer-close">
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Statut */}
      {isConfigured && (
        <div className="mx-6 mt-4 flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
          <CheckCircle2 size={13} style={{ color: '#15803D' }} />
          <span className="text-xs font-medium" style={{ color: '#15803D' }}>
            Connecté · Mis à jour le {integration.updated_at
              ? new Date(integration.updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
              : '—'}
          </span>
        </div>
      )}

      {/* Corps */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
          </div>
        ) : companies.length === 0 ? (
          <div className="rounded-xl p-4 text-sm text-center"
            style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
            <Building2 size={24} className="mx-auto mb-2 opacity-40" />
            <p className="font-medium">Aucune société configurée</p>
            <p className="text-xs mt-1">Ajoutez d'abord une société dans<br /><strong>Paramètres → Sociétés & Agences</strong></p>
          </div>
        ) : (
          <>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Configurez une clé API PennyLane pour chaque société. Chaque société peut avoir son propre dossier comptable.
            </p>
            {companies.map(comp => (
              <div key={comp.societe_id}
                className="rounded-xl p-4 space-y-3"
                style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
                {/* Nom société */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ background: color }}>
                      {(comp.nom || '?')[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{comp.nom}</p>
                    </div>
                  </div>
                  {comp.configured && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' }}>
                      Configuré
                    </span>
                  )}
                </div>
                {/* Clé API */}
                <PasswordField
                  id={`pl-key-${comp.societe_id}`}
                  label="Clé API PennyLane"
                  value={keys[comp.societe_id] || ''}
                  onChange={v => setKeys(k => ({ ...k, [comp.societe_id]: v }))}
                  placeholder="Clé API de ce dossier PennyLane"
                  required={true}
                />
                {/* Toggle : création automatique */}
                <div className="flex items-center justify-between pt-1 pb-0.5">
                  <div>
                    <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>
                      Création auto dans PennyLane
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      Crée automatiquement les nouvelles entreprises Bedee dans ce dossier PL
                    </p>
                  </div>
                  <button
                    data-testid={`pl-auto-create-toggle-${comp.societe_id}`}
                    type="button"
                    onClick={() => setAutoCreate(s => ({ ...s, [comp.societe_id]: !s[comp.societe_id] }))}
                    className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200"
                    style={{ background: autoCreate[comp.societe_id] ? color : '#D1D5DB' }}>
                    <span
                      className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200"
                      style={{ transform: autoCreate[comp.societe_id] ? 'translateX(16px)' : 'translateX(0)' }}
                    />
                  </button>
                </div>
                {/* Boutons test + sync */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid={`pl-test-btn-${comp.societe_id}`}
                    onClick={() => handleTest(comp)}
                    disabled={testing[comp.societe_id]}
                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                    style={{ border: `1px solid ${color}`, color: color, background: 'transparent' }}>
                    {testing[comp.societe_id]
                      ? <><Loader2 size={11} className="animate-spin" /> Test en cours…</>
                      : <><RefreshCw size={11} /> Tester la connexion</>
                    }
                  </button>
                  <button
                    data-testid={`pl-sync-tiers-btn-${comp.societe_id}`}
                    onClick={() => handleSyncTiers(comp)}
                    disabled={syncing[comp.societe_id] || !keys[comp.societe_id]}
                    title={!keys[comp.societe_id] ? 'Clé API requise' : 'Récupère les tiers Pennylane et les associe aux entreprises Bedee par nom'}
                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                    style={{ border: '1px solid #0EA5E9', color: '#0EA5E9', background: 'transparent', opacity: !keys[comp.societe_id] ? 0.4 : 1 }}>
                    {syncing[comp.societe_id]
                      ? <><Loader2 size={11} className="animate-spin" /> Synchro…</>
                      : <><RefreshCw size={11} /> Synchroniser les tiers</>
                    }
                  </button>
                </div>
                <TestResult result={results[comp.societe_id]} />
                {syncResults[comp.societe_id] && (() => {
                  const sr = syncResults[comp.societe_id];
                  // En cours : afficher la progression
                  if (sr.status === 'running') return (
                    <div className="text-xs mt-1 p-2 rounded-lg flex items-center gap-2" style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                      <Loader2 size={11} className="animate-spin flex-shrink-0" />
                      <span>{sr.progress || 'Synchronisation en cours…'}</span>
                    </div>
                  );
                  // Erreur
                  if (sr.status === 'error' || sr.ok === false) return (
                    <p className="text-xs mt-1" style={{ color: '#DC2626' }}>{sr.message || 'Erreur lors de la synchronisation'}</p>
                  );
                  // Terminé
                  return (
                    <div className="text-xs mt-1 p-2 rounded-lg space-y-0.5" style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' }}>
                      <div>
                        <strong>{sr.synced}</strong> associés · <strong>{sr.ambiguous}</strong> ambigus · <strong>{sr.not_found_in_bedee}</strong> non trouvés
                      </div>
                      <div style={{ color: '#6B7280' }}>
                        Pennylane : {sr.pl_customers_total ?? '?'} clients + {sr.pl_suppliers_total ?? '?'} fourn. · Bedee : {sr.bedee_total ?? '?'} entreprises
                      </div>
                      {sr.sample_unmatched?.length > 0 && (
                        <div style={{ color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 4, padding: '2px 6px', marginTop: 2 }}>
                          Exemples non trouvés : {sr.sample_unmatched.join(' · ')}
                        </div>
                      )}
                      {sr.debug_api && (
                        <div style={{ color: '#374151', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 4, padding: '2px 6px', marginTop: 2, fontFamily: 'monospace' }}>
                          API /customers → {JSON.stringify(sr.debug_api.customers)} · /suppliers → {JSON.stringify(sr.debug_api.suppliers)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
          </>
        )}

        {/* Lien doc */}
        {meta?.docs_url && (
          <a href={meta.docs_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--primary-light)' }}>
            <ExternalLink size={11} />Documentation PennyLane
          </a>
        )}
      </div>

      {/* Actions */}
      <div className="px-6 py-4 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
        <button data-testid="integration-save-btn"
          onClick={handleSave} disabled={saving || loading || companies.length === 0}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity"
          style={{ background: 'var(--primary-gradient)', opacity: (saving || companies.length === 0) ? 0.6 : 1 }}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {saving ? 'Enregistrement…' : 'Enregistrer toutes les clés'}
        </button>
        {isConfigured && (
          !confirmDelete ? (
            <button data-testid="integration-delete-btn" onClick={() => setConfirmDelete(true)}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium"
              style={{ color: '#DC2626', border: '1px solid #FECACA', background: 'transparent' }}>
              <Trash2 size={13} /> Déconnecter PennyLane
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(false)} className="flex-1 py-2 rounded-lg text-sm"
                style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}>Annuler</button>
              <button onClick={handleDelete} data-testid="integration-delete-confirm-btn"
                className="flex-1 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#DC2626' }}>
                Confirmer
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
}

// ─── Drawer générique (Odoo, Resend) ─────────────────────────────────────────

function GenericDrawer({ integration, meta, onClose, onSaved }) {
  const provider = integration?.provider;
  const color = BRAND_COLORS[provider] || '#1A3A6E';

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!meta) return;
    const init = {};
    (meta.fields || []).forEach(f => { init[f.key] = integration?.config?.[f.key] || ''; });
    setForm(init);
    setTestResult(null);
  }, [integration, meta]);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.testIntegration(provider, { config: form });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.detail || 'Erreur réseau' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveIntegration(provider, { config: form, is_active: true });
      toast.success(`${meta?.name} configuré avec succès`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteIntegration(provider);
      toast.success(`${meta?.name} déconnecté`);
      onSaved();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (!integration || !meta) return null;
  const isConfigured = !!integration.connected_at;

  return (
    <div data-testid={`integration-drawer-${provider}`}
      className="fixed inset-y-0 right-0 z-50 flex flex-col"
      style={{ width: 440, background: 'var(--card)', boxShadow: '-4px 0 40px rgba(0,0,0,0.13)', borderLeft: '1px solid var(--border)' }}>

      <div className="flex items-center gap-3 px-6 py-5" style={{ borderBottom: '1px solid var(--border)' }}>
        <ProviderLogo provider={provider} logoDomain={meta.logo_domain} name={meta.name} size={40} />
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-base" style={{ color: 'var(--text)' }}>{meta.name}</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{meta.description}</p>
        </div>
        <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-gray-100 transition-colors" data-testid="integration-drawer-close">
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>

      {isConfigured && (
        <div className="mx-6 mt-4 flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
          <CheckCircle2 size={13} style={{ color: '#15803D' }} />
          <span className="text-xs font-medium" style={{ color: '#15803D' }}>
            Connecté · Mis à jour le {integration.updated_at
              ? new Date(integration.updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
              : '—'}
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {(meta.fields || []).map(field =>
          field.type === 'password'
            ? <PasswordField key={field.key} id={field.key} label={field.label}
                value={form[field.key] || ''} onChange={v => { setForm(f => ({ ...f, [field.key]: v })); setTestResult(null); }}
                placeholder={field.placeholder} required={field.required} />
            : <TextField key={field.key} id={field.key} label={field.label}
                value={form[field.key] || ''} onChange={v => { setForm(f => ({ ...f, [field.key]: v })); setTestResult(null); }}
                placeholder={field.placeholder} required={field.required} />
        )}
        <TestResult result={testResult} />
        {meta.docs_url && (
          <a href={meta.docs_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--primary-light)' }}>
            <ExternalLink size={11} /> Documentation {meta.name}
          </a>
        )}
      </div>

      <div className="px-6 py-4 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
        <button data-testid="integration-test-btn" onClick={handleTest} disabled={testing}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium"
          style={{ border: `1px solid ${color}`, color, background: 'transparent' }}>
          {testing ? <><Loader2 size={13} className="animate-spin" /> Test en cours…</> : <><RefreshCw size={13} /> Tester la connexion</>}
        </button>
        <button data-testid="integration-save-btn" onClick={handleSave} disabled={saving}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-white"
          style={{ background: 'var(--primary-gradient)', opacity: saving ? 0.7 : 1 }}>
          {saving ? <><Loader2 size={13} className="animate-spin" /> Enregistrement…</> : 'Enregistrer'}
        </button>
        {isConfigured && (
          !confirmDelete
            ? <button data-testid="integration-delete-btn" onClick={() => setConfirmDelete(true)}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium"
                style={{ color: '#DC2626', border: '1px solid #FECACA', background: 'transparent' }}>
                <Trash2 size={13} /> Déconnecter
              </button>
            : <div className="flex gap-2">
                <button onClick={() => setConfirmDelete(false)} className="flex-1 py-2 rounded-lg text-sm"
                  style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}>Annuler</button>
                <button onClick={handleDelete} data-testid="integration-delete-confirm-btn"
                  className="flex-1 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#DC2626' }}>
                  Confirmer
                </button>
              </div>
        )}
      </div>
    </div>
  );
}

// ─── Carte provider ───────────────────────────────────────────────────────────

function ProviderCard({ integration, meta, onConfigure }) {
  const provider = integration.provider;
  const color = BRAND_COLORS[provider] || '#1A3A6E';
  const isConfigured = !!integration.connected_at;

  // Pour PennyLane multi-société, compter les sociétés configurées via le nouveau modèle
  const nbCompanies = (() => {
    if (provider !== 'pennylane') return null;
    const nbTotal = integration.config?._nb_societes;
    if (nbTotal > 0) return { configured: nbTotal, total: nbTotal };
    // Fallback legacy JSONB
    const companies = integration.config?.companies || {};
    const total = Object.keys(companies).length;
    const configured = Object.values(companies).filter(c => c.api_key).length;
    return total > 0 ? { configured, total } : null;
  })();

  return (
    <div data-testid={`integration-card-${provider}`}
      className="rounded-2xl p-5 flex flex-col gap-4 cursor-pointer group transition-all"
      style={{
        background: 'var(--card)',
        border: `1.5px solid ${isConfigured ? color + '40' : 'var(--border)'}`,
        boxShadow: isConfigured ? `0 2px 12px ${color}18` : '0 1px 4px rgba(0,0,0,0.04)',
      }}
      onClick={() => onConfigure(integration, meta)}>

      <div className="flex items-start justify-between gap-3">
        <ProviderLogo provider={provider} logoDomain={meta?.logo_domain} name={meta?.name} size={42} />
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0"
          style={isConfigured
            ? { background: '#F0FDF4', color: '#15803D' }
            : { background: 'var(--bg-subtle)', color: 'var(--text-disabled)' }}>
          {isConfigured
            ? <><CheckCircle2 size={11} /> Connecté</>
            : <><span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-50" /> Non configuré</>}
        </span>
      </div>

      <div className="flex-1">
        <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{meta?.name || provider}</p>
        <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{meta?.description}</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {meta?.category && (
            <span className="text-xs px-2 py-0.5 rounded-md font-medium"
              style={{ background: color + '15', color, border: `1px solid ${color}30` }}>
              {meta.category}
            </span>
          )}
          {nbCompanies && (
            <span className="text-xs px-2 py-0.5 rounded-md"
              style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
              {nbCompanies.configured}/{nbCompanies.total} société{nbCompanies.total > 1 ? 's' : ''}
            </span>
          )}
          {meta?.multi_company && !nbCompanies && (
            <span className="text-xs px-2 py-0.5 rounded-md"
              style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
              Multi-société
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid var(--border-light, var(--border))' }}>
        <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>
          {isConfigured && integration.updated_at
            ? `Mis à jour ${new Date(integration.updated_at).toLocaleDateString('fr-FR')}`
            : 'Cliquer pour configurer'}
        </span>
        <span className="flex items-center gap-1 text-xs font-semibold group-hover:gap-2 transition-all"
          style={{ color: isConfigured ? color : 'var(--primary-light)' }}>
          {isConfigured ? 'Modifier' : 'Connecter'}
          <ChevronRight size={13} />
        </span>
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function Integrations() {
  const [integrations, setIntegrations] = useState([]);
  const [providers, setProviders] = useState({});
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // { integration, meta }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [intRes, provRes] = await Promise.all([
        api.getIntegrations(),
        api.getIntegrationProviders(),
      ]);
      const provMap = {};
      (provRes.data.providers || []).forEach(p => { provMap[p.provider] = p; });
      setProviders(provMap);
      setIntegrations(intRes.data.integrations || []);
    } catch {
      toast.error('Impossible de charger les intégrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConfigure = (intg, meta) => setActive({ integration: intg, meta });
  const handleClose = () => setActive(null);
  const handleSaved = () => { setActive(null); load(); };

  const connected = integrations.filter(i => !!i.connected_at);
  const notConfigured = integrations.filter(i => !i.connected_at);

  const renderDrawer = () => {
    if (!active) return null;
    if (active.integration.provider === 'pennylane') {
      return <PennyLaneDrawer integration={active.integration} meta={active.meta} onClose={handleClose} onSaved={handleSaved} />;
    }
    return <GenericDrawer integration={active.integration} meta={active.meta} onClose={handleClose} onSaved={handleSaved} />;
  };

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-bold" style={{ fontSize: 22, color: 'var(--text)' }}>Intégrations</h1>
        <p className="text-sm mt-1.5" style={{ color: 'var(--text-muted)' }}>
          Connectez vos outils tiers pour automatiser la comptabilité, synchroniser votre ERP et envoyer des emails.
        </p>
        {!loading && connected.length > 0 && (
          <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-medium"
            style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' }}>
            <CheckCircle2 size={12} />
            {connected.length} intégration{connected.length > 1 ? 's' : ''} active{connected.length > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Section routage — toujours visible si des sociétés existent */}
      <RoutingSection />

      {/* Section mappings TVA */}
      <TvaMappingsSection />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
        </div>
      ) : (
        <div className="space-y-8">
          {connected.length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-widest mb-3 px-0.5"
                style={{ color: 'var(--text-disabled)' }}>Actives</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {connected.map(i => (
                  <ProviderCard key={i.provider} integration={i} meta={providers[i.provider]} onConfigure={handleConfigure} />
                ))}
              </div>
            </section>
          )}
          {notConfigured.length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-widest mb-3 px-0.5"
                style={{ color: 'var(--text-disabled)' }}>Disponibles</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {notConfigured.map(i => (
                  <ProviderCard key={i.provider} integration={i} meta={providers[i.provider]} onConfigure={handleConfigure} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {active && <div className="fixed inset-0 z-40 bg-black/25" onClick={handleClose} />}
      {renderDrawer()}
    </div>
  );
}
